<template>
  <transition name="cookie-slide">
    <div
      v-if="visible"
      class="cookie-banner"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
    >
      <h2 id="cookie-banner-title" class="cookie-banner__title">We value your privacy</h2>

      <p class="cookie-banner__text">
        We use essential cookies to run this website. With your permission we
        also use Google Analytics to understand how the site is used, and show
        videos from YouTube and Synthesia. Read our
        <a href="/privacy-policy" class="cookie-banner__link">Privacy Policy</a>.
      </p>

      <div v-if="managing" class="cookie-banner__choices">
        <label class="cookie-choice">
          <input v-model="choices.analytics" type="checkbox" class="cookie-choice__box" />
          <span><strong>Analytics</strong>: Google Analytics, to see which pages help visitors</span>
        </label>
        <label class="cookie-choice">
          <input v-model="choices.marketing" type="checkbox" class="cookie-choice__box" />
          <span><strong>Videos</strong>: the YouTube and Synthesia players</span>
        </label>
      </div>

      <div class="cookie-banner__actions">
        <button
          type="button"
          class="cookie-btn cookie-btn--reject"
          @click="reject"
        >
          Reject all
        </button>
        <button
          type="button"
          class="cookie-btn cookie-btn--accept"
          @click="acceptAll"
        >
          Accept all
        </button>
      </div>

      <button
        type="button"
        class="cookie-banner__manage"
        @click="managing ? saveChoices() : (managing = true)"
      >
        {{ managing ? 'Save my choices' : 'Manage choices' }}
      </button>
    </div>
  </transition>
</template>

<script>
import { eventBus } from "@/utils/eventBus";
import { setConsent, hasResponded, getConsent } from "@/utils/consent";
import { syncConsent } from "@/utils/trackers";

/**
 * CookieBanner
 *
 * UK PECR / UK GDPR consent banner. It shows until the visitor makes a choice
 * and remembers it (consent.js: one choice for every improve.online site).
 * Accept all and Reject all are equally easy; "Manage choices" allows each
 * category on its own. Nothing optional loads until the choice allows it
 * (trackers.js). The footer "Cookie settings" link reopens it, so withdrawing
 * is as easy as consenting.
 *
 * Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.
 */
export default {
  name: "CookieBanner",
  data() {
    return {
      visible: false,
      managing: false,
      choices: { analytics: false, marketing: false }
    };
  },
  created() {
    // Only show until the visitor has made a choice.
    if (!hasResponded()) this.open();
    eventBus.$on("open-cookie-banner", this.open);
  },
  beforeDestroy() {
    eventBus.$off("open-cookie-banner", this.open);
  },
  methods: {
    open() {
      // Start the toggles from the stored choice, so reopening shows it.
      const stored = getConsent() || {};
      this.choices = { analytics: !!stored.analytics, marketing: !!stored.marketing };
      this.managing = false;
      this.visible = true;
    },
    acceptAll() {
      this.apply({ analytics: true, marketing: true });
    },
    reject() {
      this.apply({ analytics: false, marketing: false });
    },
    saveChoices() {
      this.apply(this.choices);
    },
    apply(choice) {
      // Remember the choice, close, then load what was granted and stop what
      // was withdrawn (syncConsent deletes Google's cookies and reloads when
      // analytics was running on this page).
      setConsent(choice);
      this.visible = false;
      syncConsent();
    }
  }
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9999;
  width: 460px;
  max-width: calc(100vw - 48px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 30px rgba(0, 36, 88, 0.18);
  padding: 22px 24px;
  box-sizing: border-box;
}

.cookie-banner__title {
  font-family: "Sora", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #002458;
  margin: 0 0 8px;
  line-height: 1.3;
}

.cookie-banner__text {
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.55;
  margin: 0 0 18px;
}

.cookie-banner__link {
  color: #08a0a8;
  font-weight: 600;
  text-decoration: none;
}

.cookie-banner__link:hover {
  text-decoration: underline;
  opacity: 1;
}

.cookie-banner__actions {
  display: flex;
  gap: 12px;
}

/* Both buttons share identical size and weight so Reject is exactly as easy
   to use as Accept (UK PECR / ICO guidance). */
.cookie-btn {
  flex: 1 1 0;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cookie-btn--accept {
  background: #08a0a8;
  color: #ffffff;
  border: 1.5px solid #08a0a8;
}

.cookie-btn--accept:hover {
  background: #078289;
  border-color: #078289;
}

.cookie-btn--reject {
  background: #ffffff;
  color: #002458;
  border: 1.5px solid #002458;
}

.cookie-btn--reject:hover {
  background: #f3f4f6;
}

.cookie-btn:focus-visible,
.cookie-banner__manage:focus-visible {
  outline: 2px solid #002458;
  outline-offset: 2px;
}

.cookie-banner__choices {
  margin: 0 0 16px;
}

.cookie-choice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.45;
  margin-bottom: 10px;
  cursor: pointer;
}

.cookie-choice__box {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex: 0 0 auto;
  accent-color: #08a0a8;
}

.cookie-banner__manage {
  display: block;
  margin: 14px auto 0;
  background: none;
  border: none;
  padding: 4px 8px;
  font-family: "Sora", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #002458;
  text-decoration: underline;
  cursor: pointer;
}

/* Slide up + fade in. */
.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.cookie-slide-enter,
.cookie-slide-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (max-width: 600px) {
  .cookie-banner {
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
    max-width: none;
  }

  .cookie-banner__actions {
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cookie-slide-enter-active,
  .cookie-slide-leave-active {
    transition: opacity 0.2s ease;
  }

  .cookie-slide-enter,
  .cookie-slide-leave-to {
    transform: none;
  }
}
</style>
